<template>
  <div class="usage-dashboard">
    <section class="dashboard-metrics" aria-label="기기 사용 통계">
      <article
        v-for="metric in metrics"
        :key="metric.label"
        class="dashboard-metric"
        :class="metric.tone"
      >
        <span class="metric-symbol" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <template v-if="metric.tone === 'metric-total'">
              <rect x="3" y="4" width="18" height="13" />
              <path d="M12 17v4M7 21h10" />
            </template>
            <template v-else-if="metric.tone === 'metric-active'">
              <path d="M12 2v10M6 5a9 9 0 1 0 12 0" />
            </template>
            <template v-else-if="metric.tone === 'metric-inactive'">
              <path d="M12 2v10M6 5a9 9 0 1 0 12 0" />
            </template>
            <template v-else>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 6v6l4 2" />
            </template>
          </svg>
        </span>
        <div>
          <h3>{{ metric.label }}</h3>
          <strong>{{ metric.value }}</strong>
        </div>
      </article>
    </section>

    <section
      class="dashboard-card distribution-card"
      aria-labelledby="distribution-title"
    >
      <header class="dashboard-heading">
        <h3 id="distribution-title">사용시간분포</h3>
        <button
          type="button"
          class="dashboard-button"
          @click="$emit('statistics')"
        >
          사용시간 통계 보기
        </button>
      </header>
      <div v-if="devices.length" class="dashboard-chart">
        <Bar
          :data="chartData"
          :options="chartOptions"
          role="img"
          :aria-label="distributionLabel"
        />
      </div>
      <div v-else class="dashboard-empty chart-empty">
        조회된 기기가 없습니다.
      </div>
      <p v-if="distribution.unknown" class="dashboard-note">
        누적 사용시간 미확인 {{ distribution.unknown }}대는 구간 집계에서
        제외됩니다.
      </p>
      <details class="chart-values">
        <summary>구간별 기기 수</summary>
        <ul>
          <li v-for="(bucket, index) in buckets" :key="bucket.label">
            {{ bucket.label }}: {{ distribution.counts[index] }}대
          </li>
        </ul>
      </details>
    </section>

    <div class="dashboard-center">
      <section class="dashboard-card map-card" aria-labelledby="region-title">
        <header class="dashboard-heading">
          <div>
            <h3 id="region-title">지역별기기현황</h3>
            <p>
              {{
                selectedRegion
                  ? selectedRegion + " 선택됨"
                  : "부산 전체 · 지역을 선택하세요"
              }}
            </p>
          </div>
          <button
            class="dashboard-button"
            type="button"
            @click="$emit('region', '')"
          >
            초기화
          </button>
        </header>
        <div class="map-viewport">
          <svg
            viewBox="8 0 370 380"
            preserveAspectRatio="xMidYMid meet"
            class="busan-map"
            role="group"
            aria-label="부산 구·군별 기기 수와 지역 필터"
          >
            <g
              v-for="district in districts"
              :key="district.name"
              class="map-district"
              :class="{ selected: selectedRegion === district.name }"
              role="button"
              tabindex="0"
              :aria-label="`${district.name} ${regionCounts[district.name]}대`"
              :aria-pressed="selectedRegion === district.name"
              @click="$emit('region', district.name)"
              @keydown.enter.prevent="$emit('region', district.name)"
              @keydown.space.prevent="$emit('region', district.name)"
            >
              <title>
                {{ district.name }} {{ regionCounts[district.name] }}대
              </title>
              <path :d="district.path" />
            </g>
            <g class="map-leaders" aria-hidden="true">
              <line
                v-for="district in districts"
                :key="district.name"
                :x1="district.label[0]"
                :y1="district.label[1]"
                :x2="labelPosition(district)[0]"
                :y2="labelPosition(district)[1] + 6"
              />
            </g>
            <g
              v-for="district in districts"
              :key="'label-' + district.name"
              class="map-label"
              :class="{ selected: selectedRegion === district.name }"
              :transform="`translate(${labelPosition(district).join(',')})`"
              aria-hidden="true"
              @click="$emit('region', district.name)"
            >
              <rect
                class="map-label-hit"
                :x="-labelWidth(district) / 2"
                y="-31"
                :width="labelWidth(district)"
                height="53"
              />
              <text y="-9">{{ district.name }}</text>
              <rect
                :x="-countWidth(district) / 2"
                y="-2"
                :width="countWidth(district)"
                height="22"
                rx="11"
              />
              <text class="map-count" y="14">
                {{ regionCounts[district.name] }}
              </text>
            </g>
          </svg>
        </div>
        <p class="dashboard-note">
          {{ selectedRegion ? selectedRegion + " · " : "" }}검색·지역 조건이
          적용된 전체 기기 기준
        </p>
      </section>

      <section
        class="dashboard-card overuse-card"
        aria-labelledby="overuse-title"
      >
        <header class="dashboard-heading">
          <div>
            <h3 id="overuse-title">초과사용목록</h3>
            <p>24시간 이상 연속 활성 · {{ overuse.length }}대</p>
          </div>
        </header>
        <div class="dashboard-scroll">
          <table class="dashboard-table overuse-table">
            <thead>
              <tr>
                <th>학교명</th>
                <th>학교지역</th>
                <th>위치</th>
                <th>상태지속시간</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="device in overuse"
                :key="device.device_uuid"
                class="overuse-row"
                tabindex="0"
                @click="$emit('detail', device)"
                @keydown.enter="$emit('detail', device)"
                @keydown.space.prevent="$emit('detail', device)"
              >
                <td>{{ device.school_name || "-" }}</td>
                <td>{{ device.school_region || "-" }}</td>
                <td>{{ device.app_location || "-" }}</td>
                <td class="overuse-hours">{{ duration(device) }}</td>
              </tr>
              <tr v-if="!overuse.length">
                <td colspan="4" class="dashboard-empty">
                  24시간 이상 연속 사용 중인 기기가 없습니다.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <section
      class="dashboard-card board-list-card"
      aria-labelledby="board-list-title"
    >
      <header class="dashboard-heading">
        <div>
          <h3 id="board-list-title">전자칠판 목록</h3>
          <p class="list-total">총 {{ devices.length }}대 · {{ range }}</p>
        </div>
        <button
          class="dashboard-button"
          type="button"
          :disabled="!devices.length"
          @click="$emit('pdf')"
        >
          PDF 미리보기
        </button>
      </header>
      <div class="dashboard-scroll">
        <table class="dashboard-table board-table">
          <thead>
            <tr>
              <th>
                <button @click="$emit('sort', 'status')">
                  상태 {{ sortIcon("status") }}
                </button>
              </th>
              <th>
                <button @click="$emit('sort', 'schoolName')">
                  학교명 {{ sortIcon("schoolName") }}
                </button>
              </th>
              <th>
                <button @click="$emit('sort', 'schoolRegion')">
                  학교지역 {{ sortIcon("schoolRegion") }}
                </button>
              </th>
              <th>위치</th>
              <th>
                <button @click="$emit('sort', 'totalUsage')">
                  총사용시간 {{ sortIcon("totalUsage") }}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="device in pageDevices"
              :key="device.device_uuid"
              class="board-row"
              tabindex="0"
              @click="$emit('detail', device)"
              @keydown.enter="$emit('detail', device)"
              @keydown.space.prevent="$emit('detail', device)"
            >
              <td>
                <span
                  class="board-status"
                  :class="{ active: isActive(device) }"
                  :title="deviceState(device) || '분류 확인 불가'"
                  >{{ isActive(device) ? "활성화" : "비활성화" }}</span
                >
              </td>
              <td>{{ device.school_name || "-" }}</td>
              <td>{{ device.school_region || "-" }}</td>
              <td>{{ device.app_location || "-" }}</td>
              <td>{{ formatUsage(device.total_usage_sec) }}</td>
            </tr>
            <tr v-if="!devices.length">
              <td colspan="5" class="dashboard-empty">
                검색 조건에 맞는 기기가 없습니다.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <nav class="dashboard-pagination" aria-label="기기 목록 페이지">
        <button
          type="button"
          :disabled="page === 1"
          @click="$emit('page', page - 1)"
        >
          이전</button
        ><span>{{ page }} / {{ totalPages }}</span
        ><button
          type="button"
          :disabled="page === totalPages"
          @click="$emit('page', page + 1)"
        >
          다음
        </button>
      </nav>
    </section>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed } from "vue";
import { Bar } from "vue-chartjs";
import districts from "@/assets/busanDistricts.json";
import { USAGE_BUCKETS } from "@/utils/usageStatistics";
import { statusHours } from "@/utils/deviceStatus";
import {
  overuseDevices,
  dashboardDistribution,
  dashboardRegionCounts
} from "@/utils/usageDashboard";

const props = defineProps({
  devices: { type: Array, required: true },
  selectedRegion: { type: String, default: "" },
  page: { type: Number, required: true },
  pageDevices: { type: Array, required: true },
  totalPages: { type: Number, required: true },
  range: { type: String, required: true },
  usageSummary: { type: Object, required: true },
  statusSummary: { type: Object, required: true },
  formatUsage: { type: Function, required: true },
  deviceState: { type: Function, required: true },
  sortIcon: { type: Function, required: true }
});
defineEmits(["detail", "statistics", "region", "pdf", "page", "sort"]);
const buckets = USAGE_BUCKETS;
const isActive = (device) =>
  String(device.status || "").toUpperCase() === "ACTIVE";
const overuse = computed(() => overuseDevices(props.devices));
const distribution = computed(() => dashboardDistribution(props.devices));
const regionCounts = computed(() => dashboardRegionCounts(props.devices));
const duration = (device) => `${Math.floor(statusHours(device))}시간`;
const metrics = computed(() => {
  const count = props.usageSummary.count;
  const active = props.statusSummary.active;
  return [
    {
      label: "전체설치기기",
      value: `${count.toLocaleString()}대`,
      icon: "▣",
      tone: "metric-total"
    },
    {
      label: "활성화",
      value: `${active.toLocaleString()}대`,
      icon: "⏻",
      tone: "metric-active"
    },
    {
      label: "비활성화",
      value: `${(count - active).toLocaleString()}대`,
      icon: "!",
      tone: "metric-inactive"
    },
    {
      label: "평균 사용시간",
      value: props.usageSummary.averageText,
      icon: "◷",
      tone: "metric-average"
    }
  ];
});
const chartData = computed(() => ({
  labels: [
    ["0–20,000", "시간 미만"],
    ["20,000–30,000", "시간 미만"],
    ["30,000–40,000", "시간 미만"],
    ["40,000–50,000", "시간 미만"],
    ["50,000시간", "이상"]
  ],
  datasets: [
    {
      label: "기기 수",
      data: distribution.value.counts,
      backgroundColor: buckets.map((bucket) => bucket.color),
      maxBarThickness: 40
    }
  ]
}));
const distributionLabel = computed(() =>
  buckets
    .map(
      (bucket, index) =>
        `${bucket.label}: ${distribution.value.counts[index]}대`
    )
    .join(", ")
);
const chartOptions = {
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: { title: (items) => buckets[items[0].dataIndex].label }
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 9 }, maxRotation: 0, autoSkip: false }
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: "기기 수" },
      ticks: { precision: 0 },
      suggestedMax: 1
    }
  }
};
// Separate labels in the small, adjacent downtown districts without changing boundaries.
const labelOverrides = {
  중구: [210, 295],
  서구: [145, 295],
  동구: [200, 240],
  영도구: [245, 350],
  부산진구: [181, 185],
  동래구: [246, 130],
  남구: [275, 295],
  북구: [164, 125],
  해운대구: [322, 185],
  사하구: [120, 350],
  금정구: [229, 75],
  강서구: [60, 240],
  연제구: [245, 185],
  수영구: [290, 240],
  사상구: [115, 195],
  기장군: [315, 65]
};
function labelWidth(district) {
  return Math.max(countWidth(district), district.name.length * 14) + 8;
}
function countWidth(district) {
  return Math.max(
    34,
    String(regionCounts.value[district.name]).length * 8 + 12
  );
}
function labelPosition(district) {
  return labelOverrides[district.name] || district.label;
}
</script>

<style scoped>
.usage-dashboard {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-template-rows: 300px 390px;
  gap: 16px;
  min-width: 0;
}
.dashboard-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dededb;
  padding: 16px;
}
.dashboard-center {
  grid-column: 5 / 9;
  grid-row: 1 / 3;
  display: grid;
  grid-template-rows: minmax(0, 60fr) minmax(0, 40fr);
  gap: 16px;
  min-width: 0;
  min-height: 0;
}
.distribution-card {
  grid-column: 1 / 5;
  grid-row: 2;
}
.dashboard-metrics {
  grid-column: 1 / 5;
  grid-row: 1;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 12px;
  min-width: 0;
}
.dashboard-metric {
  grid-column: span 6;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid #dededb;
  min-width: 0;
  overflow: hidden;
}
.dashboard-metric > div {
  min-width: 0;
}
.dashboard-metric h3 {
  margin: 0 0 9px;
  font-size: 12px;
  color: #545b68;
  font-weight: 600;
}
.dashboard-metric strong {
  display: block;
  color: #111;
  font-size: clamp(17px, 1.45vw, 25px);
  overflow-wrap: anywhere;
  line-height: 1.35;
}
.metric-symbol {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  font-size: 25px;
  color: #0066ff;
  background: #edf5ff;
}
.metric-inactive .metric-symbol {
  background: #f1f2f3;
  color: #737980;
}
.metric-average .metric-symbol {
  font-size: 24px;
}
.board-list-card {
  grid-column: 9 / 13;
  grid-row: 1 / 3;
}
.dashboard-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 8px;
  margin-bottom: 12px;
  min-height: 34px;
}
.dashboard-heading h3 {
  margin: 0;
  color: #171717;
  font-size: 16px;
  font-weight: 800;
  white-space: nowrap;
}
.dashboard-heading p {
  margin: 5px 0 0;
  color: #737373;
  font-size: 11px;
  line-height: 1.4;
}
.dashboard-button {
  border: 1px solid #dededb;
  background: #fff;
  color: #333;
  padding: 7px 9px;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
}
.dashboard-button:hover {
  background: #f5f5f3;
}
.dashboard-button:disabled {
  opacity: 0.45;
  cursor: default;
}
.dashboard-scroll {
  min-height: 0;
  min-width: 0;
  flex: 1;
  overflow: auto;
  border: 1px solid #e7e7e4;
}
.dashboard-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}
.overuse-table {
  min-width: 320px;
}
.board-table {
  min-width: 440px;
}
.dashboard-table th {
  position: sticky;
  top: 0;
  z-index: 1;
}
.dashboard-table th button {
  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.dashboard-table tbody tr {
  cursor: pointer;
}
.dashboard-table tbody tr:hover {
  background: #f7f8fa;
}
.dashboard-table tbody tr:focus-visible {
  outline: 2px solid #171717;
  outline-offset: -2px;
}
.dashboard-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  padding-top: 14px;
  font-size: 12px;
}
.dashboard-pagination button {
  background: #fff;
  border: 1px solid #dededb;
  padding: 6px 10px;
  cursor: pointer;
}
.dashboard-pagination button:disabled {
  opacity: 0.35;
  cursor: default;
}
.board-status {
  display: inline-block;
  color: #777;
  background: #f0f0ee;
  padding: 4px 5px;
  font-size: 10px;
  white-space: nowrap;
}
.board-status.active {
  color: #1260cd;
  background: #edf4ff;
}
.dashboard-empty {
  color: #888;
  text-align: center;
  font-size: 12px;
  padding: 22px 10px;
  cursor: default;
}
.dashboard-chart {
  min-height: 0;
  flex: 1;
  position: relative;
}
.chart-empty {
  flex: 1;
  display: grid;
  place-items: center;
}
.dashboard-note {
  color: #8a8a8a;
  font-size: 10px;
  margin: 7px 0 0;
  line-height: 1.4;
}
.chart-values {
  flex-shrink: 0;
  font-size: 10px;
  color: #777;
  max-height: 70px;
  overflow: auto;
  margin-top: 6px;
}
.chart-values summary {
  cursor: pointer;
}
.chart-values ul {
  margin: 5px 0;
  padding-left: 18px;
}
.map-viewport {
  min-height: 0;
  flex: 1;
  display: grid;
  place-items: center;
}
.busan-map {
  width: 100%;
  height: 100%;
}
.map-district {
  cursor: pointer;
}
.map-district path {
  fill: #eaf4ff;
  stroke: #b6d5fa;
  stroke-width: 0.7;
  transition: fill 0.15s;
}
.map-district:hover path,
.map-district:focus-visible path {
  fill: #c9e1ff;
  stroke: #0066ff;
  stroke-width: 1.4;
}
.map-district.selected path {
  fill: #aacfff;
  stroke: #0066ff;
  stroke-width: 1.5;
}
.map-leaders {
  pointer-events: none;
  stroke: #91acc9;
  stroke-width: 0.7;
}
.map-label {
  cursor: pointer;
}
.map-label text {
  font-size: 14px;
  text-anchor: middle;
  fill: #354153;
  font-weight: 600;
}
.map-label rect {
  fill: #0066ff;
}
.map-label.selected rect {
  fill: #171717;
}
.map-label .map-label-hit,
.map-label.selected .map-label-hit {
  fill: transparent;
  pointer-events: all;
}
.map-label .map-count {
  fill: #fff;
  font-size: 13px;
}
.metric-symbol svg {
  width: 24px;
  height: 24px;
}
.metric-average strong {
  font-size: clamp(14px, 1vw, 18px);
  overflow-wrap: normal;
  word-break: keep-all;
}
@media (max-width: 1100px) {
  .usage-dashboard {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: 300px 390px 520px;
  }
  .dashboard-center {
    grid-column: 2;
    grid-template-rows: minmax(0, 1fr) 275px;
  }
  .dashboard-metrics {
    grid-column: 1;
  }
  .distribution-card {
    grid-column: 1;
  }
  .board-list-card {
    grid-column: 1 / 3;
    grid-row: 3;
  }
}
@media (max-width: 640px) {
  .usage-dashboard {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 240px 340px auto 520px;
    gap: 12px;
  }
  .dashboard-center {
    grid-column: 1;
    grid-row: 3;
    grid-template-rows: 420px 280px;
    gap: 12px;
  }
  .dashboard-metrics {
    grid-column: 1;
    grid-row: 1;
  }
  .distribution-card {
    grid-column: 1;
    grid-row: 2;
  }
  .board-list-card {
    grid-column: 1;
    grid-row: 4;
  }
  .dashboard-card {
    padding: 12px;
  }
  .dashboard-metric {
    padding: 12px;
    gap: 8px;
  }
  .dashboard-metric strong {
    font-size: 19px;
  }
}
</style>

<style>
/* Shared admin table rules are !important; scope compact overrides to this dashboard. */
.admin-layout .usage-dashboard .dashboard-table th {
  padding: 10px 7px !important;
  font-size: 10px !important;
  background: #f5f5f3 !important;
  letter-spacing: 0;
}
.admin-layout .usage-dashboard .dashboard-table td {
  padding: 11px 7px !important;
  font-size: 11px !important;
  line-height: 1.5 !important;
  white-space: normal;
  overflow-wrap: anywhere;
}
.admin-layout .usage-dashboard .dashboard-table .overuse-hours {
  color: #c3322b !important;
  font-weight: 700;
}
</style>
